import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import Layout from '../layouts/Dashboard/Layout';
import { getTvListCall, updateTvCall, getTvAdsCall, storeTvAdCall, updateTvAdCall, deleteTvAdCall, uploadTvAdImageCall, uploadTvLogoCall, getCurrentTokenClaims } from "../../api/Tv";
import LivePlayer from "../common/LivePlayer";
import FullMobilePreview from "../common/FullMobilePreview";

const presetBanners = [
    { label: "Prime News & Live Coverage", path: "posters/1.jpg" },
    { label: "Cinema & Movie Festival", path: "posters/2.jpg" },
    { label: "Live Regional Sports Tournament", path: "posters/3.jpg" },
    { label: "Commercial Sponsor Banner", path: "posters/4.jpg" }
];

const StationStudio = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const tokenClaims = getCurrentTokenClaims();
    const isClient = tokenClaims?.role === 'client' || Boolean(tokenClaims?.tv_id);
    const clientTvId = tokenClaims?.tv_id;

    const [stations, setStations] = useState([]);
    const [selectedStation, setSelectedStation] = useState(null);
    const [loadingStations, setLoadingStations] = useState(true);
    const initialTab = searchParams.get("tab") === "stream" ? "stream" : "banners";
    const [activeTab, setActiveTab] = useState(initialTab); // "banners" or "stream"
    const [isLivePlayerOpen, setIsLivePlayerOpen] = useState(false);

    // Banners state
    const [ads, setAds] = useState([]);
    const [loadingAds, setLoadingAds] = useState(false);
    const [isAddBannerModalOpen, setIsAddBannerModalOpen] = useState(false);
    const [bannerMode, setBannerMode] = useState("url"); // "url" or "upload"
    const [bannerFile, setBannerFile] = useState(null);
    const [bannerFilePreview, setBannerFilePreview] = useState("");
    const [bannerForm, setBannerForm] = useState({
        poster: "posters/1.jpg",
        order: 1,
        status: 1,
        customUrl: ""
    });
    const [bannerSubmitting, setBannerSubmitting] = useState(false);

    // Edit Banner state
    const [isEditBannerModalOpen, setIsEditBannerModalOpen] = useState(false);
    const [editBannerMode, setEditBannerMode] = useState("url"); // "url" or "upload"
    const [editBannerFile, setEditBannerFile] = useState(null);
    const [editBannerFilePreview, setEditBannerFilePreview] = useState("");
    const [editingBanner, setEditingBanner] = useState({
        id: null,
        poster: "",
        customUrl: "",
        order: 1,
        status: 1
    });
    const [bannerUpdating, setBannerUpdating] = useState(false);

    // Stream profile state
    const [stationForm, setStationForm] = useState({
        name: "",
        logo: "/images/logos/appletv.png",
        code: "",
        live: "",
        coverage: "",
        website: "",
        email: "",
        mobile: "",
        disclaimer: "",
        status: 1,
        social_medias: {
            whatsapp: "",
            youtube: "",
            instagram: "",
            facebook: "",
            twitter: "",
            website: ""
        }
    });
    const [stationSaving, setStationSaving] = useState(false);
    const [logoMode, setLogoMode] = useState("url"); // "url" or "upload"
    const [logoFile, setLogoFile] = useState(null);
    const [logoFilePreview, setLogoFilePreview] = useState("");
    const [logoUploading, setLogoUploading] = useState(false);

    // Mobile simulator slide index
    const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
    const [toastMessage, setToastMessage] = useState(null);

    const showToast = (msg) => {
        setToastMessage(msg);
        setTimeout(() => setToastMessage(null), 3500);
    };

    // Load stations (automatically scoped by server based on Bearer token)
    const loadStations = (targetId = null, isBackground = false) => {
        if (!isBackground) setLoadingStations(true);
        getTvListCall()
            .then(res => {
                const list = res?.data?.data?.data || res?.data?.data || [];
                const channelList = Array.isArray(list) ? list : [];
                setStations(channelList);

                if (channelList.length > 0) {
                    const savedStationId = localStorage.getItem("nanba_selected_station_id");
                    const queryId = targetId || searchParams.get("id") || selectedStation?.id || savedStationId;
                    let station = channelList.find(c => String(c.id) === String(queryId)) || channelList[0];
                    if (isBackground) {
                        setSelectedStation(prev => ({ ...prev, ...station }));
                    } else {
                        selectStation(station);
                    }
                }
            })
            .catch(err => {
                console.error("Failed to load stations:", err);
            })
            .finally(() => {
                if (!isBackground) setLoadingStations(false);
            });
    };

    useEffect(() => {
        loadStations();
        const handleTabSwitch = (e) => {
            if (e.detail) setActiveTab(e.detail);
        };
        window.addEventListener("switchStudioTab", handleTabSwitch);

        const timer = setInterval(() => {
            loadStations(null, true);
        }, 5000);

        return () => {
            window.removeEventListener("switchStudioTab", handleTabSwitch);
            clearInterval(timer);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Set selected station & load ads
    const selectStation = (station) => {
        setSelectedStation(station);
        setSearchParams({ id: station.id });
        try {
            localStorage.setItem("nanba_selected_station_id", String(station.id));
        } catch (e) {}
        const socialMap = {};
        (station.tv_social_medias || []).forEach(sm => {
            const code = (sm.code || sm['code '] || '').trim().toLowerCase();
            if (code) socialMap[code] = sm.link || '';
        });
        setLogoMode("url");
        setLogoFile(null);
        setLogoFilePreview("");
        setStationForm({
            id: station.id,
            name: station.name || "",
            logo: station.logo || "/images/logos/appletv.png",
            code: station.code || "",
            live: station.live || "",
            coverage: station.coverage || "",
            website: station.website || "",
            email: station.email || "",
            mobile: station.mobile || "",
            disclaimer: station.disclaimer || "",
            status: station.status !== undefined ? station.status : 1,
            social_medias: {
                whatsapp: socialMap.whatsapp || (station.mobile ? `https://wa.me/${station.mobile}` : ""),
                youtube: socialMap.youtube || "",
                instagram: socialMap.instagram || "",
                facebook: socialMap.facebook || "",
                twitter: socialMap.twitter || "",
                website: socialMap.website || station.website || ""
            }
        });
        loadStationAds(station.id);
    };

    // Load ads for a station
    const loadStationAds = (tvId) => {
        setLoadingAds(true);
        getTvAdsCall(tvId)
            .then(res => {
                const list = res?.data?.data || [];
                const sorted = Array.isArray(list) ? list.sort((a, b) => (a.order || 0) - (b.order || 0)) : [];
                setAds(sorted);
                setCurrentSlideIndex(0);
            })
            .catch(err => {
                console.error("Failed to load ads:", err);
                setAds([]);
            })
            .finally(() => {
                setLoadingAds(false);
            });
    };

    // Mobile simulator auto-slide timer
    useEffect(() => {
        if (ads.length <= 1) return;
        const timer = setInterval(() => {
            setCurrentSlideIndex(prev => (prev + 1) % ads.length);
        }, 3200);
        return () => clearInterval(timer);
    }, [ads.length]);

    // Format poster URL
    const getPosterUrl = (posterPath) => {
        if (!posterPath) return "https://public.nanbateam.com/posters/1.jpg";
        if (posterPath.startsWith("http://") || posterPath.startsWith("https://") || posterPath.startsWith("data:image/")) {
            return posterPath;
        }
        return `https://public.nanbateam.com/${posterPath.replace(/^\/+/, '').replace(/^images\//, '')}`;
    };

    // File selection handler for local image upload
    const handleBannerFileSelect = (e, isEdit = false) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith("image/")) {
            alert("Please select a valid image file (JPG, PNG, WebP, GIF).");
            return;
        }

        if (file.size > 10 * 1024 * 1024) {
            alert("File is too large. Maximum allowed size is 10MB.");
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            const result = reader.result;
            if (isEdit) {
                setEditBannerFile(file);
                setEditBannerFilePreview(result);
            } else {
                setBannerFile(file);
                setBannerFilePreview(result);
            }
        };
        reader.readAsDataURL(file);
    };

    // Add Banner handler (Supports both URL and direct File Upload)
    const handleAddBanner = async (e) => {
        e.preventDefault();
        if (!selectedStation) return;

        let posterToUse = "";

        if (bannerMode === "upload") {
            if (!bannerFilePreview) {
                alert("Please select an image file to upload.");
                return;
            }
            setBannerSubmitting(true);
            try {
                const uploadRes = await uploadTvAdImageCall({
                    tv_id: selectedStation.id,
                    image: bannerFilePreview,
                    filename: bannerFile?.name || "banner.jpg"
                });
                posterToUse = uploadRes.data?.data?.url;
                if (!posterToUse) {
                    throw new Error("Failed to obtain public URL for uploaded banner.");
                }
            } catch (err) {
                alert(err.response?.data?.message || err.message || "Image upload failed. Please try again.");
                setBannerSubmitting(false);
                return;
            }
        } else {
            posterToUse = bannerForm.customUrl.trim() || bannerForm.poster;
            if (!posterToUse) {
                alert("Please enter a valid image URL or choose a preset.");
                return;
            }
        }

        setBannerSubmitting(true);
        storeTvAdCall({
            tv_id: selectedStation.id,
            poster: posterToUse,
            order: parseInt(bannerForm.order, 10) || 1,
            status: parseInt(bannerForm.status, 10) !== 0 ? 1 : 0
        })
            .then(() => {
                setIsAddBannerModalOpen(false);
                setBannerForm({ poster: "posters/1.jpg", order: ads.length + 1, status: 1, customUrl: "" });
                setBannerFile(null);
                setBannerFilePreview("");
                setBannerMode("url");
                showToast("Banner image added to mobile app carousel!");
                loadStationAds(selectedStation.id);
            })
            .catch(err => {
                alert(err.response?.data?.message || err.message || "Failed to add banner");
            })
            .finally(() => {
                setBannerSubmitting(false);
            });
    };

    // Toggle banner status (enable / disable)
    const handleToggleBannerStatus = (ad) => {
        const nextStatus = ad.status === 0 ? 1 : 0;
        updateTvAdCall({ id: ad.id, status: nextStatus })
            .then(() => {
                showToast(nextStatus === 1 ? "Banner enabled for mobile app!" : "Banner disabled (hidden from mobile app)!");
                if (selectedStation) loadStationAds(selectedStation.id);
            })
            .catch(err => {
                alert(err.response?.data?.message || err.message || "Failed to update banner status");
            });
    };

    // Open Edit Banner Modal
    const handleOpenEditBannerModal = (ad) => {
        setEditingBanner({
            id: ad.id,
            poster: ad.poster || "posters/1.jpg",
            customUrl: (ad.poster?.startsWith("http://") || ad.poster?.startsWith("https://")) ? ad.poster : "",
            order: ad.order || 1,
            status: ad.status !== undefined && ad.status !== null ? ad.status : 1
        });
        setEditBannerMode("url");
        setEditBannerFile(null);
        setEditBannerFilePreview("");
        setIsEditBannerModalOpen(true);
    };

    // Save Edited Banner (Supports both URL and direct File Upload)
    const handleSaveEditBanner = async (e) => {
        e.preventDefault();
        let posterToUse = "";

        if (editBannerMode === "upload") {
            if (!editBannerFilePreview) {
                alert("Please select a replacement image file to upload.");
                return;
            }
            setBannerUpdating(true);
            try {
                const uploadRes = await uploadTvAdImageCall({
                    tv_id: selectedStation?.id || 1,
                    image: editBannerFilePreview,
                    filename: editBannerFile?.name || "banner.jpg"
                });
                posterToUse = uploadRes.data?.data?.url;
                if (!posterToUse) {
                    throw new Error("Failed to obtain public URL for uploaded banner.");
                }
            } catch (err) {
                alert(err.response?.data?.message || err.message || "Image upload failed. Please try again.");
                setBannerUpdating(false);
                return;
            }
        } else {
            posterToUse = editingBanner.customUrl.trim() || editingBanner.poster;
            if (!posterToUse) {
                alert("Please enter a valid image URL.");
                return;
            }
        }

        setBannerUpdating(true);
        updateTvAdCall({
            id: editingBanner.id,
            poster: posterToUse,
            order: parseInt(editingBanner.order, 10) || 1,
            status: parseInt(editingBanner.status, 10)
        })
            .then(() => {
                setIsEditBannerModalOpen(false);
                setEditBannerFile(null);
                setEditBannerFilePreview("");
                setEditBannerMode("url");
                showToast("Banner updated successfully!");
                if (selectedStation) loadStationAds(selectedStation.id);
            })
            .catch(err => {
                alert(err.response?.data?.message || err.message || "Failed to update banner");
            })
            .finally(() => {
                setBannerUpdating(false);
            });
    };

    // Delete Banner handler
    const handleDeleteBanner = (adId) => {
        if (!window.confirm("Remove this banner image from mobile app scroll carousel?")) return;
        deleteTvAdCall({ id: adId })
            .then(() => {
                showToast("Banner removed from mobile app!");
                if (selectedStation) loadStationAds(selectedStation.id);
            })
            .catch(err => {
                alert(err.response?.data?.message || err.message || "Failed to delete banner");
            });
    };

    // TV Station Logo File Select
    const handleLogoFileSelect = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith("image/") && !file.name.endsWith(".svg")) {
            alert("Please select a valid image file (PNG, SVG, JPG, WebP, GIF).");
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            alert("Logo file is too large. Maximum allowed size is 5MB.");
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            setLogoFile(file);
            setLogoFilePreview(reader.result);
        };
        reader.readAsDataURL(file);
    };

    // TV Station Logo Direct Upload Button handler
    const handleUploadLogoNow = async () => {
        if (!logoFilePreview) {
            alert("Please select a logo file first.");
            return;
        }
        setLogoUploading(true);
        try {
            const targetTvId = stationForm.id || selectedStation?.id;
            const uploadRes = await uploadTvLogoCall({
                tv_id: targetTvId,
                image: logoFilePreview,
                filename: logoFile?.name || "logo.png"
            });
            const uploadedUrl = uploadRes.data?.data?.url;
            if (uploadedUrl) {
                setStationForm(prev => ({ ...prev, logo: uploadedUrl }));
                setSelectedStation(prev => prev ? ({ ...prev, logo: uploadedUrl }) : prev);
                setLogoFile(null);
                setLogoFilePreview("");
                setLogoMode("url");

                // Update userInfo in localStorage so any components reading it have fresh logo
                try {
                    const storedUser = JSON.parse(localStorage.getItem('user_info') || '{}');
                    if (storedUser.station) {
                        storedUser.station.logo = uploadedUrl;
                        localStorage.setItem('user_info', JSON.stringify(storedUser));
                    }
                } catch (e) {}

                // Dispatch global event so SideBar and header logos update immediately
                window.dispatchEvent(new CustomEvent("stationLogoUpdated", { detail: { logo: uploadedUrl, name: stationForm.name } }));

                showToast("TV Station Logo uploaded and updated across the panel!");
                loadStations(targetTvId);
            } else {
                throw new Error("Failed to obtain public URL for uploaded logo.");
            }
        } catch (err) {
            alert(err.response?.data?.message || err.message || "Logo upload failed. Please try again.");
        } finally {
            setLogoUploading(false);
        }
    };

    // Save Station Details handler (auto uploads logo if a new file was chosen)
    const handleSaveStation = async (e) => {
        e.preventDefault();
        setStationSaving(true);

        let formToSave = { ...stationForm };

        if (logoMode === "upload" && logoFilePreview) {
            try {
                const uploadRes = await uploadTvLogoCall({
                    tv_id: stationForm.id || selectedStation?.id,
                    image: logoFilePreview,
                    filename: logoFile?.name || "logo.png"
                });
                const uploadedUrl = uploadRes.data?.data?.url;
                if (uploadedUrl) {
                    formToSave.logo = uploadedUrl;
                    setStationForm(prev => ({ ...prev, logo: uploadedUrl }));
                    setLogoFile(null);
                    setLogoFilePreview("");
                    setLogoMode("url");
                }
            } catch (err) {
                console.warn("Logo auto-upload failed, passing image data to save endpoint:", err);
                formToSave.logo = logoFilePreview;
            }
        }

        updateTvCall(formToSave)
            .then(() => {
                showToast("Station broadcast details updated successfully!");

                // Update userInfo in localStorage
                try {
                    const storedUser = JSON.parse(localStorage.getItem('user_info') || '{}');
                    if (storedUser.station) {
                        storedUser.station.name = formToSave.name;
                        if (formToSave.logo) storedUser.station.logo = formToSave.logo;
                        localStorage.setItem('user_info', JSON.stringify(storedUser));
                    }
                } catch (e) {}

                // Dispatch global event so SideBar updates immediately
                window.dispatchEvent(new CustomEvent("stationLogoUpdated", { detail: { logo: formToSave.logo, name: formToSave.name } }));

                loadStations(formToSave.id);
            })
            .catch(err => {
                alert(err.response?.data?.message || err.message || "Failed to save station details");
            })
            .finally(() => {
                setStationSaving(false);
            });
    };

    return (
        <Layout>
            {/* Header */}
            <div className="page-header" style={{ marginBottom: "16px" }}>
                <div className="page-title-group">
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
                        <span className="station-badge-pill" style={isClient ? { background: "rgba(6, 182, 212, 0.2)", color: "#22d3ee", border: "1px solid rgba(6, 182, 212, 0.4)" } : {}}>
                            {isClient ? "Station Owner Portal" : "NanbaTeam.com Platform"}
                        </span>
                        <span style={{ fontSize: "12px", color: "var(--text-muted)" }}>
                            {isClient ? "• Dedicated Single TV Control Room" : "• Client TV Station Control Room"}
                        </span>
                    </div>
                    <h1>{selectedStation?.name ? `${selectedStation.name} Studio` : "Client Station Studio"}</h1>
                    <p>
                        {isClient
                            ? "Dedicated channel workspace to manage your live broadcast stream, mobile banner ads, and station information"
                            : "Dedicated station management for live broadcast stream, mobile app banner ads, and station settings"}
                    </p>
                </div>

                {/* Station Dropdown Selector / Client Lock Badge */}
                {isClient ? (
                    <div style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "10px",
                        background: "rgba(6, 182, 212, 0.12)",
                        border: "1px solid rgba(6, 182, 212, 0.3)",
                        padding: "8px 16px",
                        borderRadius: "10px",
                        color: "#22d3ee",
                        fontWeight: 600,
                        fontSize: "13px"
                    }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                            <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                        </svg>
                        <span>{selectedStation?.name || "Single Station"} &bull; Station Owner Workspace {clientTvId ? `(ID #${clientTvId})` : ""}</span>
                    </div>
                ) : (
                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        <span style={{ fontSize: "13px", color: "var(--text-muted)", fontWeight: 600 }}>Active Station:</span>
                        <select
                            className="form-control"
                            style={{ width: "240px", height: "42px", fontWeight: 600 }}
                            value={selectedStation?.id || ""}
                            disabled={loadingStations}
                            onChange={(e) => {
                                const found = stations.find(s => String(s.id) === String(e.target.value));
                                if (found) selectStation(found);
                            }}
                        >
                            {stations.map(st => (
                                <option key={st.id} value={st.id}>
                                    {st.name} ({st.code})
                                </option>
                            ))}
                        </select>
                    </div>
                )}
            </div>

            {/* Selected Station Banner Info */}
            {selectedStation && (
                <div style={{
                    backgroundColor: "var(--bg-surface)",
                    border: "1px solid var(--border-subtle)",
                    borderRadius: "14px",
                    padding: "16px 20px",
                    marginBottom: "24px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: "16px"
                }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
                        <div className="channel-avatar" style={{ width: "46px", height: "46px", fontSize: "16px" }}>
                            {selectedStation.name ? selectedStation.name.substring(0, 2).toUpperCase() : "TV"}
                        </div>
                        <div>
                            <div style={{ fontSize: "17px", fontWeight: 700, color: "#fff" }}>
                                {selectedStation.name}
                            </div>
                            <div style={{ fontSize: "12px", color: "var(--text-muted)", display: "flex", gap: "10px", marginTop: "2px" }}>
                                <span>Station Code: <strong style={{ color: "var(--accent-violet)" }}>{selectedStation.code}</strong></span>
                                <span>&bull;</span>
                                <span>Region: <strong style={{ color: "var(--text-secondary)" }}>{selectedStation.coverage || "Tamil Nadu"}</strong></span>
                            </div>
                        </div>
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        <div style={{ fontSize: "12px", color: "var(--text-muted)", maxWidth: "300px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            RTMP: <code style={{ color: "var(--accent-cyan)" }}>{selectedStation.live}</code>
                        </div>
                        <span className="badge-status active">Broadcasting</span>
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => setIsLivePlayerOpen(true)}
                            style={{
                                padding: "6px 12px",
                                fontSize: "12px",
                                background: "rgba(16, 185, 129, 0.15)",
                                borderColor: "rgba(16, 185, 129, 0.4)",
                                color: "#10b981",
                                fontWeight: 700,
                                display: "flex",
                                alignItems: "center",
                                gap: "6px"
                            }}
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                <polygon points="5 3 19 12 5 21 5 3"></polygon>
                            </svg>
                            Live Monitor
                        </button>
                    </div>
                </div>
            )}

            {/* Navigation Tabs */}
            <div className="tabs-nav">
                <button
                    className={`tab-btn ${activeTab === "banners" ? "active" : ""}`}
                    onClick={() => setActiveTab("banners")}
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                        <circle cx="8.5" cy="8.5" r="1.5"></circle>
                        <polyline points="21 15 16 10 5 21"></polyline>
                    </svg>
                    <span>Mobile App Scrolling Banners ({ads.length})</span>
                </button>
                <button
                    className={`tab-btn ${activeTab === "mobile-full" ? "active" : ""}`}
                    onClick={() => setActiveTab("mobile-full")}
                    style={{
                        background: activeTab === "mobile-full" ? "var(--accent-gradient)" : "rgba(99, 102, 241, 0.1)",
                        borderColor: activeTab === "mobile-full" ? "transparent" : "rgba(99, 102, 241, 0.3)",
                        color: activeTab === "mobile-full" ? "#fff" : "var(--accent-cyan)",
                        fontWeight: 700
                    }}
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect>
                        <line x1="12" y1="18" x2="12.01" y2="18"></line>
                    </svg>
                    <span>📱 Full View Mobile Design</span>
                </button>
                <button
                    className={`tab-btn ${activeTab === "stream" ? "active" : ""}`}
                    onClick={() => setActiveTab("stream")}
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polygon points="5 3 19 12 5 21 5 3"></polygon>
                    </svg>
                    <span>Live Stream & Station Settings</span>
                </button>
            </div>

            {/* TAB 1: MOBILE APP SCROLLING BANNERS */}
            {activeTab === "banners" && (
                <div className="banner-manager-layout">
                    {/* Left: Banner Cards List */}
                    <div>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                            <div>
                                <h3 style={{ fontSize: "16px", fontWeight: 700, color: "#fff" }}>
                                    Active Mobile App Banners
                                </h3>
                                <p style={{ fontSize: "12.5px", color: "var(--text-muted)", marginTop: "2px" }}>
                                    These poster banners scroll automatically on the mobile app home screen for <strong>{selectedStation?.name}</strong>
                                </p>
                            </div>
                            <button className="btn btn-primary btn-sm" onClick={() => setIsAddBannerModalOpen(true)}>
                                + Add Banner Image
                            </button>
                        </div>

                        {loadingAds ? (
                            <div style={{ padding: "40px", textAlign: "center", color: "var(--text-muted)" }}>
                                Loading banners for this station...
                            </div>
                        ) : ads.length > 0 ? (
                            <div className="banner-cards-list">
                                {ads.map((ad, index) => (
                                    <div className="banner-item-card" key={ad.id}>
                                        <img
                                            src={getPosterUrl(ad.poster)}
                                            alt={`Banner ${index + 1}`}
                                            className="banner-item-img"
                                            onError={(e) => {
                                                e.target.src = "http://localhost:8080/images/posters/1.jpg";
                                            }}
                                        />
                                        <div className="banner-item-details">
                                            <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                                                <h4 style={{ margin: 0 }}>Mobile Banner #{index + 1}</h4>
                                                <span
                                                    className={`badge-status ${ad.status !== 0 ? "active" : "inactive"}`}
                                                    style={{
                                                        fontSize: "10.5px",
                                                        padding: "2px 8px",
                                                        background: ad.status !== 0 ? "rgba(16, 185, 129, 0.15)" : "rgba(239, 68, 68, 0.15)",
                                                        color: ad.status !== 0 ? "#10b981" : "#f87171",
                                                        borderColor: ad.status !== 0 ? "rgba(16, 185, 129, 0.4)" : "rgba(239, 68, 68, 0.4)"
                                                    }}
                                                >
                                                    {ad.status !== 0 ? "● Active / Enabled" : "○ Disabled (Hidden)"}
                                                </span>
                                            </div>
                                            <p title={ad.poster} style={{ marginTop: "4px" }}>Path: {ad.poster}</p>
                                            <div style={{ display: "flex", gap: "8px", marginTop: "6px" }}>
                                                <span className="banner-order-pill">
                                                    Scroll Order: #{ad.order || (index + 1)}
                                                </span>
                                                <span className="port-tag">
                                                    API Key: {selectedStation?.code}
                                                </span>
                                            </div>
                                        </div>
                                        <div style={{ display: "flex", gap: "6px", flexShrink: 0, alignItems: "center" }}>
                                            <button
                                                type="button"
                                                className={`btn btn-sm ${ad.status !== 0 ? "btn-secondary" : "btn-primary"}`}
                                                onClick={() => handleToggleBannerStatus(ad)}
                                                title={ad.status !== 0 ? "Disable this banner (hide from mobile app)" : "Enable this banner for mobile app"}
                                                style={{ fontSize: "12px", whiteSpace: "nowrap" }}
                                            >
                                                {ad.status !== 0 ? "Disable" : "Enable"}
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-secondary btn-sm"
                                                onClick={() => handleOpenEditBannerModal(ad)}
                                                title="Edit banner image, order or status"
                                                style={{ fontSize: "12px" }}
                                            >
                                                Edit
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-danger btn-sm"
                                                onClick={() => handleDeleteBanner(ad.id)}
                                                title="Delete Banner"
                                                style={{ fontSize: "12px" }}
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div style={{
                                padding: "48px 24px",
                                textAlign: "center",
                                backgroundColor: "var(--bg-surface)",
                                border: "1px dashed var(--border-subtle)",
                                borderRadius: "14px"
                            }}>
                                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="1.5" style={{ marginBottom: "12px" }}>
                                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                                    <circle cx="8.5" cy="8.5" r="1.5"></circle>
                                    <polyline points="21 15 16 10 5 21"></polyline>
                                </svg>
                                <div style={{ fontSize: "15px", fontWeight: 600, color: "#fff", marginBottom: "4px" }}>
                                    No Banners Configured
                                </div>
                                <p style={{ fontSize: "13px", color: "var(--text-muted)", maxWidth: "380px", margin: "0 auto 16px" }}>
                                    Add banners for <strong>{selectedStation?.name}</strong> to enable the auto-scrolling image carousel on the mobile app.
                                </p>
                                <button className="btn btn-primary btn-sm" onClick={() => setIsAddBannerModalOpen(true)}>
                                    + Add First Banner Image
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Right: Mobile Phone Live Preview */}
                    <div className="mobile-simulator-card" style={{ width: "320px", alignSelf: "flex-start", padding: "16px" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", marginBottom: "14px" }}>
                            <div>
                                <div style={{ fontSize: "13.5px", fontWeight: 700, color: "#fff" }}>
                                    Mobile App Live Preview
                                </div>
                                <div style={{ fontSize: "11px", color: "var(--text-muted)" }}>
                                    Simulated <code>TvAddsPagerAdapter</code>
                                </div>
                            </div>
                            <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                onClick={() => setActiveTab("mobile-full")}
                                style={{
                                    fontSize: "11px",
                                    padding: "5px 10px",
                                    color: "var(--accent-cyan)",
                                    borderColor: "rgba(6, 182, 212, 0.4)",
                                    background: "rgba(6, 182, 212, 0.12)",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "5px",
                                    fontWeight: 700
                                }}
                            >
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                    <polyline points="15 3 21 3 21 9"></polyline>
                                    <polyline points="9 21 3 21 3 15"></polyline>
                                    <line x1="21" y1="3" x2="14" y2="10"></line>
                                    <line x1="3" y1="21" x2="10" y2="14"></line>
                                </svg>
                                Full View
                            </button>
                        </div>

                        {/* Enhanced compact mobile phone preview */}
                        <FullMobilePreview
                            station={{
                                ...selectedStation,
                                logo: (logoMode === "upload" && logoFilePreview) ? logoFilePreview : (stationForm.logo || selectedStation?.logo),
                                name: stationForm.name || selectedStation?.name
                            }}
                            ads={ads}
                            currentSlideIndex={currentSlideIndex}
                            setCurrentSlideIndex={setCurrentSlideIndex}
                            isFullViewMode={false}
                        />

                        <div style={{ width: "100%", marginTop: "14px", textAlign: "center" }}>
                            <button
                                type="button"
                                className="btn btn-primary btn-sm"
                                onClick={() => setActiveTab("mobile-full")}
                                style={{ width: "100%", fontSize: "12px", padding: "8px" }}
                            >
                                📱 Switch to Full View Mobile Design
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* TAB 2: STATION STREAM & DETAILS */}
            {activeTab === "stream" && (
                <div style={{ maxWidth: "720px", backgroundColor: "var(--bg-surface)", border: "1px solid var(--border-subtle)", borderRadius: "14px", padding: "24px" }}>
                    <div style={{ marginBottom: "20px" }}>
                        <h3 style={{ fontSize: "17px", fontWeight: 700, color: "#fff" }}>
                            Live Broadcast & Stream Endpoint
                        </h3>
                        <p style={{ fontSize: "13px", color: "var(--text-muted)", marginTop: "2px" }}>
                            Update the RTMP live stream URL, station branding, and contact credentials for <strong>{selectedStation?.name}</strong>
                        </p>
                    </div>

                    <form onSubmit={handleSaveStation}>
                        <div className="form-group" style={{ marginBottom: "16px" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                                <label className="form-label" style={{ margin: 0 }}>Live RTMP / HLS Stream URL *</label>
                                <button
                                    type="button"
                                    onClick={() => setStationForm({ ...stationForm, live: "rtmp://localhost:1935/live/stream" })}
                                    style={{
                                        background: "none",
                                        border: "none",
                                        color: "var(--accent-cyan)",
                                        fontSize: "12px",
                                        cursor: "pointer",
                                        textDecoration: "underline"
                                    }}
                                >
                                    + Load Local Sample Movie Stream
                                </button>
                            </div>
                            <div style={{ display: "flex", gap: "8px" }}>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={stationForm.live}
                                    onChange={(e) => setStationForm({ ...stationForm, live: e.target.value })}
                                    placeholder="rtmp://upload.live.nanbatv.com/live/stream"
                                    style={{ fontFamily: "var(--font-mono)", fontSize: "13px" }}
                                    required
                                />
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setIsLivePlayerOpen(true)}
                                    style={{ whiteSpace: "nowrap" }}
                                >
                                    Live Monitor
                                </button>
                            </div>
                            <div style={{ fontSize: "11px", color: "var(--text-muted)", marginTop: "4px" }}>
                                This stream is distributed to all Android TV & Mobile clients. Local test feed: <code>rtmp://localhost:1935/live/stream</code>.
                            </div>
                        </div>

                        <div className="form-row" style={{ marginBottom: "16px" }}>
                            <div className="form-group">
                                <label className="form-label">Station Name</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={stationForm.name}
                                    onChange={(e) => setStationForm({ ...stationForm, name: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Station Code (Unique)</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={stationForm.code}
                                    onChange={(e) => setStationForm({ ...stationForm, code: e.target.value })}
                                    required
                                />
                            </div>
                        </div>

                        <div className="form-row" style={{ marginBottom: "16px" }}>
                            <div className="form-group">
                                <label className="form-label">Coverage Region</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={stationForm.coverage}
                                    onChange={(e) => setStationForm({ ...stationForm, coverage: e.target.value })}
                                    placeholder="e.g. Tamil Nadu, Vedasandur"
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Official Website</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={stationForm.website}
                                    onChange={(e) => setStationForm({ ...stationForm, website: e.target.value })}
                                    placeholder="https://example.com"
                                />
                            </div>
                        </div>

                        {/* TV Station Brand Logo */}
                        <div className="form-group" style={{ marginBottom: "22px" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                                <label className="form-label" style={{ margin: 0, fontWeight: 700 }}>
                                    📺 TV Station Brand Logo
                                </label>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setStationForm({ ...stationForm, logo: "/images/logos/appletv.png" });
                                        setLogoFile(null);
                                        setLogoFilePreview("");
                                        setLogoMode("url");
                                    }}
                                    style={{
                                        background: "none",
                                        border: "none",
                                        color: "var(--accent-cyan)",
                                        fontSize: "12px",
                                        cursor: "pointer",
                                        textDecoration: "underline"
                                    }}
                                >
                                    + Use Default Nanba / AppleTV Logo
                                </button>
                            </div>

                            {/* Mode toggle: URL vs Upload */}
                            <div style={{
                                display: "flex",
                                gap: "8px",
                                marginBottom: "12px",
                                padding: "4px",
                                background: "rgba(255, 255, 255, 0.04)",
                                borderRadius: "8px",
                                border: "1px solid var(--border-subtle)",
                                maxWidth: "340px"
                            }}>
                                <button
                                    type="button"
                                    className={`tab-btn ${logoMode === "url" ? "active" : ""}`}
                                    style={{
                                        flex: 1,
                                        padding: "6px 10px",
                                        borderRadius: "6px",
                                        fontSize: "12px",
                                        fontWeight: "600",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: "5px"
                                    }}
                                    onClick={() => setLogoMode("url")}
                                >
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
                                    <span>Logo URL</span>
                                </button>
                                <button
                                    type="button"
                                    className={`tab-btn ${logoMode === "upload" ? "active" : ""}`}
                                    style={{
                                        flex: 1,
                                        padding: "6px 10px",
                                        borderRadius: "6px",
                                        fontSize: "12px",
                                        fontWeight: "600",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: "5px"
                                    }}
                                    onClick={() => setLogoMode("upload")}
                                >
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                                    <span>Upload File</span>
                                </button>
                            </div>

                            {/* Mode Content */}
                            {logoMode === "url" ? (
                                <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                                    <div style={{
                                        width: "56px",
                                        height: "56px",
                                        borderRadius: "12px",
                                        background: "#030712",
                                        border: "1px solid var(--border-subtle)",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        overflow: "hidden",
                                        flexShrink: 0
                                    }}>
                                        {stationForm.logo ? (
                                            <img
                                                src={getPosterUrl(stationForm.logo)}
                                                alt="Logo Preview"
                                                style={{ width: "100%", height: "100%", objectFit: "contain" }}
                                                onError={(e) => { e.target.style.display = "none"; }}
                                            />
                                        ) : (
                                            <span style={{ fontSize: "11px", color: "var(--text-muted)" }}>No Logo</span>
                                        )}
                                    </div>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={stationForm.logo || ""}
                                        onChange={(e) => setStationForm({ ...stationForm, logo: e.target.value })}
                                        placeholder="/images/logos/appletv.png or https://example.com/logo.png"
                                        style={{ fontFamily: "var(--font-mono)", fontSize: "13px" }}
                                    />
                                </div>
                            ) : (
                                <div style={{ display: "flex", gap: "14px", alignItems: "center" }}>
                                    <div style={{
                                        width: "60px",
                                        height: "60px",
                                        borderRadius: "12px",
                                        background: "#030712",
                                        border: "1px solid var(--border-subtle)",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        overflow: "hidden",
                                        flexShrink: 0
                                    }}>
                                        {(logoFilePreview || stationForm.logo) ? (
                                            <img
                                                src={logoFilePreview || getPosterUrl(stationForm.logo)}
                                                alt="Logo Preview"
                                                style={{ width: "100%", height: "100%", objectFit: "contain" }}
                                            />
                                        ) : (
                                            <span style={{ fontSize: "11px", color: "var(--text-muted)" }}>No Logo</span>
                                        )}
                                    </div>

                                    <div style={{ flex: 1 }}>
                                        <div
                                            style={{
                                                border: "2px dashed var(--border-subtle)",
                                                borderRadius: "10px",
                                                padding: "12px 14px",
                                                textAlign: "center",
                                                cursor: "pointer",
                                                background: logoFile ? "rgba(16, 185, 129, 0.08)" : "rgba(255, 255, 255, 0.02)",
                                                transition: "all 0.2s"
                                            }}
                                            onClick={() => document.getElementById("station-logo-file-input")?.click()}
                                        >
                                            <input
                                                id="station-logo-file-input"
                                                type="file"
                                                accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                                                style={{ display: "none" }}
                                                onChange={handleLogoFileSelect}
                                            />
                                            <div style={{ fontSize: "13px", fontWeight: "600", color: "#f8fafc" }}>
                                                {logoFile ? `Selected: ${logoFile.name}` : "Click to Browse TV Logo (PNG, SVG, JPG, WebP)"}
                                            </div>
                                            <div style={{ fontSize: "11px", color: "var(--text-muted)", marginTop: "2px" }}>
                                                {logoFile ? `${(logoFile.size / 1024).toFixed(1)} KB — Will upload on Save or click Upload Now` : "Recommended: 256x256 or 512x512 transparent PNG/SVG (Max 5MB)"}
                                            </div>
                                        </div>
                                    </div>

                                    {logoFile && (
                                        <button
                                            type="button"
                                            className="btn btn-primary"
                                            disabled={logoUploading}
                                            onClick={handleUploadLogoNow}
                                            style={{ fontSize: "12px", padding: "8px 14px", whiteSpace: "nowrap" }}
                                        >
                                            {logoUploading ? "Uploading..." : "Upload Now"}
                                        </button>
                                    )}
                                </div>
                            )}

                            <div style={{ fontSize: "11px", color: "var(--text-muted)", marginTop: "6px" }}>
                                Displayed in the mobile app header, Android TV status bar, and station directory. Default: <code>/images/logos/appletv.png</code>.
                            </div>
                        </div>

                        {/* Social Media & Mobile App Links Section */}
                        <div style={{
                            marginTop: "20px",
                            marginBottom: "20px",
                            padding: "18px",
                            borderRadius: "12px",
                            border: "1px solid var(--border-subtle)",
                            background: "rgba(255, 255, 255, 0.02)"
                        }}>
                            <div style={{ marginBottom: "14px" }}>
                                <h4 style={{ fontSize: "14px", fontWeight: 700, color: "#fff", margin: 0 }}>
                                    📱 Social Media & Mobile App Quick Links
                                </h4>
                                <p style={{ fontSize: "12px", color: "var(--text-muted)", margin: "2px 0 0" }}>
                                    These links are loaded dynamically by the Android app (WhatsApp, YouTube, Instagram, Facebook, X / Twitter, Website)
                                </p>
                            </div>

                            <div className="form-row" style={{ marginBottom: "12px" }}>
                                <div className="form-group">
                                    <label className="form-label">🟢 WhatsApp Link / Number</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={stationForm.social_medias?.whatsapp || ""}
                                        onChange={(e) => setStationForm({
                                            ...stationForm,
                                            social_medias: { ...stationForm.social_medias, whatsapp: e.target.value }
                                        })}
                                        placeholder="https://wa.me/9080736170"
                                        style={{ fontSize: "12.5px" }}
                                    />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">🔴 YouTube Channel Link</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={stationForm.social_medias?.youtube || ""}
                                        onChange={(e) => setStationForm({
                                            ...stationForm,
                                            social_medias: { ...stationForm.social_medias, youtube: e.target.value }
                                        })}
                                        placeholder="https://youtube/@nanbatv"
                                        style={{ fontSize: "12.5px" }}
                                    />
                                </div>
                            </div>

                            <div className="form-row" style={{ marginBottom: "12px" }}>
                                <div className="form-group">
                                    <label className="form-label">🟣 Instagram Profile Link</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={stationForm.social_medias?.instagram || ""}
                                        onChange={(e) => setStationForm({
                                            ...stationForm,
                                            social_medias: { ...stationForm.social_medias, instagram: e.target.value }
                                        })}
                                        placeholder="https://instagram.com/nanbatv"
                                        style={{ fontSize: "12.5px" }}
                                    />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">🔵 Facebook Page Link</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={stationForm.social_medias?.facebook || ""}
                                        onChange={(e) => setStationForm({
                                            ...stationForm,
                                            social_medias: { ...stationForm.social_medias, facebook: e.target.value }
                                        })}
                                        placeholder="https://facebook.com/nanbatv"
                                        style={{ fontSize: "12.5px" }}
                                    />
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label className="form-label">⚫ Twitter / X Link</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={stationForm.social_medias?.twitter || ""}
                                        onChange={(e) => setStationForm({
                                            ...stationForm,
                                            social_medias: { ...stationForm.social_medias, twitter: e.target.value }
                                        })}
                                        placeholder="https://twitter.com/nanbatv"
                                        style={{ fontSize: "12.5px" }}
                                    />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">🌐 Web Link</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={stationForm.social_medias?.website || ""}
                                        onChange={(e) => setStationForm({
                                            ...stationForm,
                                            social_medias: { ...stationForm.social_medias, website: e.target.value }
                                        })}
                                        placeholder="https://nanbatv.com"
                                        style={{ fontSize: "12.5px" }}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="form-row" style={{ marginBottom: "16px" }}>
                            <div className="form-group">
                                <label className="form-label">Official Email</label>
                                <input
                                    type="email"
                                    className="form-control"
                                    value={stationForm.email}
                                    onChange={(e) => setStationForm({ ...stationForm, email: e.target.value })}
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Contact Mobile</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={stationForm.mobile}
                                    onChange={(e) => setStationForm({ ...stationForm, mobile: e.target.value })}
                                />
                            </div>
                        </div>

                        <div className="form-group" style={{ marginBottom: "24px" }}>
                            <label className="form-label">Station Disclaimer</label>
                            <input
                                type="text"
                                className="form-control"
                                value={stationForm.disclaimer}
                                onChange={(e) => setStationForm({ ...stationForm, disclaimer: e.target.value })}
                                placeholder="Optional broadcasting disclaimer"
                            />
                        </div>

                        <button type="submit" className="btn btn-primary" disabled={stationSaving}>
                            {stationSaving ? "Saving Updates..." : "Save Station Settings"}
                        </button>
                    </form>
                </div>
            )}

            {/* TAB 3: FULL VIEW MOBILE DESIGN STUDIO */}
            {activeTab === "mobile-full" && (
                <div style={{
                    display: "grid",
                    gridTemplateColumns: "minmax(390px, 460px) 1fr",
                    gap: "36px",
                    alignItems: "start"
                }}>
                    {/* Left/Center: Full Scale Mobile Chassis Stage */}
                    <div style={{
                        backgroundColor: "var(--bg-surface)",
                        border: "1px solid var(--border-subtle)",
                        borderRadius: "24px",
                        padding: "24px 20px",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        boxShadow: "0 10px 40px rgba(0, 0, 0, 0.45)"
                    }}>
                        <FullMobilePreview
                            station={{
                                ...selectedStation,
                                logo: (logoMode === "upload" && logoFilePreview) ? logoFilePreview : (stationForm.logo || selectedStation?.logo),
                                name: stationForm.name || selectedStation?.name
                            }}
                            ads={ads}
                            currentSlideIndex={currentSlideIndex}
                            setCurrentSlideIndex={setCurrentSlideIndex}
                            isFullViewMode={true}
                            onCloseFullView={() => setActiveTab("banners")}
                        />
                    </div>

                    {/* Right: Station Telemetry, Quick Controls & Banner Picker */}
                    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                        {/* Live Telemetry Card */}
                        <div style={{
                            backgroundColor: "var(--bg-surface)",
                            border: "1px solid var(--border-subtle)",
                            borderRadius: "16px",
                            padding: "22px"
                        }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "16px", flexWrap: "wrap", gap: "10px" }}>
                                <div>
                                    <div style={{ fontSize: "11px", color: "var(--accent-cyan)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.8px" }}>
                                        Mobile Stream Telemetry
                                    </div>
                                    <h3 style={{ fontSize: "22px", fontWeight: 800, color: "#fff", marginTop: "2px" }}>
                                        {selectedStation?.name}
                                    </h3>
                                    <div style={{ fontSize: "12px", color: "var(--text-muted)", marginTop: "2px" }}>
                                        Code: <strong style={{ color: "var(--accent-violet)" }}>{selectedStation?.code}</strong> &bull; Region: <strong>{selectedStation?.coverage || "Tamil Nadu"}</strong>
                                    </div>
                                </div>
                                <span className="badge-status active" style={{ padding: "6px 14px", fontSize: "12px", letterSpacing: "0.5px" }}>
                                    ● LIVE ON AIR
                                </span>
                            </div>

                            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "12px", marginBottom: "18px" }}>
                                <div style={{ background: "rgba(255, 255, 255, 0.03)", padding: "12px", borderRadius: "10px", border: "1px solid var(--border-subtle)" }}>
                                    <div style={{ fontSize: "11px", color: "var(--text-muted)" }}>HLS Live Feed</div>
                                    <div style={{ fontSize: "12px", fontWeight: 700, color: "var(--accent-cyan)", marginTop: "4px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                        :8888/live/stream/
                                    </div>
                                </div>
                                <div style={{ background: "rgba(255, 255, 255, 0.03)", padding: "12px", borderRadius: "10px", border: "1px solid var(--border-subtle)" }}>
                                    <div style={{ fontSize: "11px", color: "var(--text-muted)" }}>Playback Protocol</div>
                                    <div style={{ fontSize: "12px", fontWeight: 700, color: "#10b981", marginTop: "4px" }}>
                                        ExoPlayer (RTMP / HLS)
                                    </div>
                                </div>
                                <div style={{ background: "rgba(255, 255, 255, 0.03)", padding: "12px", borderRadius: "10px", border: "1px solid var(--border-subtle)" }}>
                                    <div style={{ fontSize: "11px", color: "var(--text-muted)" }}>Carousel Banners</div>
                                    <div style={{ fontSize: "12px", fontWeight: 700, color: "var(--accent-violet)", marginTop: "4px" }}>
                                        {ads.length} Active Banners
                                    </div>
                                </div>
                                <div style={{ background: "rgba(255, 255, 255, 0.03)", padding: "12px", borderRadius: "10px", border: "1px solid var(--border-subtle)" }}>
                                    <div style={{ fontSize: "11px", color: "var(--text-muted)" }}>Sponsor Phone</div>
                                    <div style={{ fontSize: "12px", fontWeight: 700, color: "#fff", marginTop: "4px" }}>
                                        {selectedStation?.mobile || "9080736170"}
                                    </div>
                                </div>
                            </div>

                            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setIsLivePlayerOpen(true)}
                                    style={{ fontSize: "12px" }}
                                >
                                    🖥️ Open Web Stream Monitor
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setActiveTab("banners")}
                                    style={{ fontSize: "12px" }}
                                >
                                    🖼️ Manage Banners List
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setActiveTab("stream")}
                                    style={{ fontSize: "12px" }}
                                >
                                    ⚙️ Edit Station Settings
                                </button>
                            </div>
                        </div>

                        {/* Interactive Banner Slider Quick Selector */}
                        <div style={{
                            backgroundColor: "var(--bg-surface)",
                            border: "1px solid var(--border-subtle)",
                            borderRadius: "16px",
                            padding: "22px"
                        }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                                <div>
                                    <h4 style={{ fontSize: "16px", fontWeight: 700, color: "#fff", margin: 0 }}>
                                        Live Carousel Switcher ({ads.length})
                                    </h4>
                                    <p style={{ fontSize: "12px", color: "var(--text-muted)", margin: "2px 0 0" }}>
                                        Click any banner card below to instantly display it on the mobile device
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-primary btn-sm"
                                    onClick={() => setIsAddBannerModalOpen(true)}
                                >
                                    + Add Banner
                                </button>
                            </div>

                            {ads.length > 0 ? (
                                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))", gap: "12px" }}>
                                    {ads.map((ad, idx) => (
                                        <div
                                            key={ad.id}
                                            onClick={() => setCurrentSlideIndex(idx)}
                                            style={{
                                                cursor: "pointer",
                                                borderRadius: "12px",
                                                overflow: "hidden",
                                                border: idx === currentSlideIndex ? "2px solid var(--accent-cyan)" : "1px solid var(--border-subtle)",
                                                background: idx === currentSlideIndex ? "rgba(6, 182, 212, 0.12)" : "rgba(255, 255, 255, 0.02)",
                                                padding: "6px",
                                                transition: "all 0.2s ease",
                                                transform: idx === currentSlideIndex ? "scale(1.03)" : "none",
                                                boxShadow: idx === currentSlideIndex ? "0 4px 14px rgba(6, 182, 212, 0.3)" : "none"
                                            }}
                                        >
                                            <div style={{ height: "70px", borderRadius: "8px", overflow: "hidden", position: "relative" }}>
                                                <img
                                                    src={getPosterUrl(ad.poster)}
                                                    alt={`Banner ${idx + 1}`}
                                                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                                    onError={(e) => { e.target.src = "http://localhost:8080/images/posters/1.jpg"; }}
                                                />
                                                <span style={{
                                                    position: "absolute",
                                                    top: "4px",
                                                    left: "4px",
                                                    background: "rgba(0, 0, 0, 0.75)",
                                                    color: "#fff",
                                                    fontSize: "9px",
                                                    fontWeight: 800,
                                                    padding: "1px 5px",
                                                    borderRadius: "4px"
                                                }}>
                                                    #{ad.order || idx + 1}
                                                </span>
                                                {idx === currentSlideIndex && (
                                                    <span style={{
                                                        position: "absolute",
                                                        bottom: "4px",
                                                        right: "4px",
                                                        background: "var(--accent-cyan)",
                                                        color: "#000",
                                                        fontSize: "8.5px",
                                                        fontWeight: 800,
                                                        padding: "1px 5px",
                                                        borderRadius: "4px"
                                                    }}>
                                                        ON SCREEN
                                                    </span>
                                                )}
                                            </div>
                                            <div style={{ fontSize: "11px", fontWeight: 600, color: idx === currentSlideIndex ? "var(--accent-cyan)" : "var(--text-secondary)", marginTop: "6px", textAlign: "center" }}>
                                                Slide {idx + 1}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div style={{ textAlign: "center", padding: "24px", color: "var(--text-muted)", fontSize: "13px" }}>
                                    No banners configured. Click <strong>+ Add Banner</strong> to test mobile carousel.
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* ADD BANNER MODAL */}
            {isAddBannerModalOpen && (
                <div className="modal-overlay" onClick={() => setIsAddBannerModalOpen(false)}>
                    <div className="modal-box" style={{ maxWidth: "520px" }} onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Add Banner for {selectedStation?.name}</h3>
                            <button className="modal-close-btn" onClick={() => setIsAddBannerModalOpen(false)}>&times;</button>
                        </div>
                        <form onSubmit={handleAddBanner}>
                            <div className="modal-body">
                                <p style={{ fontSize: "13px", color: "var(--text-muted)", margin: "0 0 14px 0" }}>
                                    Add a banner poster for the mobile app carousel via direct Image URL or by uploading a file from your device.
                                </p>

                                {/* Banner Source Type Toggle */}
                                <div style={{ display: "flex", gap: "8px", marginBottom: "16px", padding: "4px", background: "rgba(255,255,255,0.04)", borderRadius: "10px", border: "1px solid var(--border-subtle)" }}>
                                    <button
                                        type="button"
                                        className={`tab-btn ${bannerMode === "url" ? "active" : ""}`}
                                        style={{ flex: 1, padding: "8px 12px", borderRadius: "8px", fontSize: "13px", fontWeight: "600", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}
                                        onClick={() => setBannerMode("url")}
                                    >
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
                                        <span>Image URL / Preset</span>
                                    </button>
                                    <button
                                        type="button"
                                        className={`tab-btn ${bannerMode === "upload" ? "active" : ""}`}
                                        style={{ flex: 1, padding: "8px 12px", borderRadius: "8px", fontSize: "13px", fontWeight: "600", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}
                                        onClick={() => setBannerMode("upload")}
                                    >
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                                        <span>Upload File</span>
                                    </button>
                                </div>

                                {bannerMode === "url" ? (
                                    <>
                                        <div className="form-group">
                                            <label className="form-label">Select Platform Preset Banner</label>
                                            <select
                                                className="form-control"
                                                value={bannerForm.poster}
                                                onChange={(e) => setBannerForm({ ...bannerForm, poster: e.target.value, customUrl: "" })}
                                            >
                                                {presetBanners.map((p, idx) => (
                                                    <option key={idx} value={p.path}>
                                                        {p.label} ({p.path})
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="form-group">
                                            <label className="form-label">Or Custom Web Image URL</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="e.g. https://domain.com/banner.jpg or posters/1.jpg"
                                                value={bannerForm.customUrl}
                                                onChange={(e) => setBannerForm({ ...bannerForm, customUrl: e.target.value })}
                                            />
                                        </div>
                                    </>
                                ) : (
                                    <div className="form-group">
                                        <label className="form-label">Choose Image from Device</label>
                                        <div
                                            style={{
                                                border: "2px dashed var(--border-subtle)",
                                                borderRadius: "10px",
                                                padding: "20px 14px",
                                                textAlign: "center",
                                                cursor: "pointer",
                                                background: bannerFile ? "rgba(16, 185, 129, 0.08)" : "rgba(255, 255, 255, 0.02)",
                                                transition: "all 0.2s"
                                            }}
                                            onClick={() => document.getElementById("add-banner-file-input")?.click()}
                                        >
                                            <input
                                                id="add-banner-file-input"
                                                type="file"
                                                accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
                                                style={{ display: "none" }}
                                                onChange={(e) => handleBannerFileSelect(e, false)}
                                            />
                                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={bannerFile ? "var(--accent-emerald)" : "var(--primary-color)"} strokeWidth="1.8" style={{ margin: "0 auto 8px", display: "block" }}>
                                                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                                                <circle cx="8.5" cy="8.5" r="1.5"/>
                                                <polyline points="21 15 16 10 5 21"/>
                                            </svg>
                                            <div style={{ fontSize: "13.5px", fontWeight: "600", color: "#f8fafc", marginBottom: "4px" }}>
                                                {bannerFile ? bannerFile.name : "Click to Browse or Drag Image File"}
                                            </div>
                                            <div style={{ fontSize: "12px", color: "var(--text-muted)" }}>
                                                {bannerFile ? `${(bannerFile.size / 1024).toFixed(1)} KB — Click to choose different file` : "Supports JPG, PNG, WebP, GIF (Max 10MB)"}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">Display Scroll Order (1 = First Banner)</label>
                                        <input
                                            type="number"
                                            min="1"
                                            className="form-control"
                                            value={bannerForm.order}
                                            onChange={(e) => setBannerForm({ ...bannerForm, order: e.target.value })}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Initial Status</label>
                                        <select
                                            className="form-control"
                                            value={bannerForm.status}
                                            onChange={(e) => setBannerForm({ ...bannerForm, status: parseInt(e.target.value, 10) })}
                                        >
                                            <option value={1}>🟢 Enabled (Active)</option>
                                            <option value={0}>⚪ Disabled (Hidden)</option>
                                        </select>
                                    </div>
                                </div>

                                {/* Preview of banner to add */}
                                <div>
                                    <div style={{ fontSize: "12px", color: "var(--text-muted)", marginBottom: "6px", display: "flex", justifyContent: "space-between" }}>
                                        <span>Banner Image Preview:</span>
                                        {bannerMode === "upload" && bannerFile && (
                                            <span style={{ color: "var(--accent-emerald)", fontWeight: "600" }}>✓ Ready to upload to public CDN</span>
                                        )}
                                    </div>
                                    <div style={{ height: "110px", borderRadius: "8px", overflow: "hidden", border: "1px solid var(--border-subtle)", background: "rgba(0,0,0,0.2)" }}>
                                        <img
                                            src={bannerMode === "upload" ? (bannerFilePreview || getPosterUrl(bannerForm.poster)) : getPosterUrl(bannerForm.customUrl.trim() || bannerForm.poster)}
                                            alt="Preview"
                                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                            onError={(e) => {
                                                e.target.src = "https://public.nanbateam.com/posters/1.jpg";
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setIsAddBannerModalOpen(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-primary" disabled={bannerSubmitting}>
                                    {bannerSubmitting ? (bannerMode === "upload" ? "Uploading & Adding..." : "Adding...") : "Add to Mobile App Carousel"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* EDIT BANNER MODAL */}
            {isEditBannerModalOpen && (
                <div className="modal-overlay" onClick={() => setIsEditBannerModalOpen(false)}>
                    <div className="modal-box" style={{ maxWidth: "520px" }} onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Edit Mobile Banner #{editingBanner.id}</h3>
                            <button className="modal-close-btn" onClick={() => setIsEditBannerModalOpen(false)}>&times;</button>
                        </div>
                        <form onSubmit={handleSaveEditBanner}>
                            <div className="modal-body">
                                {/* Banner Source Type Toggle */}
                                <div style={{ display: "flex", gap: "8px", marginBottom: "16px", padding: "4px", background: "rgba(255,255,255,0.04)", borderRadius: "10px", border: "1px solid var(--border-subtle)" }}>
                                    <button
                                        type="button"
                                        className={`tab-btn ${editBannerMode === "url" ? "active" : ""}`}
                                        style={{ flex: 1, padding: "8px 12px", borderRadius: "8px", fontSize: "13px", fontWeight: "600", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}
                                        onClick={() => setEditBannerMode("url")}
                                    >
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
                                        <span>Current / New URL</span>
                                    </button>
                                    <button
                                        type="button"
                                        className={`tab-btn ${editBannerMode === "upload" ? "active" : ""}`}
                                        style={{ flex: 1, padding: "8px 12px", borderRadius: "8px", fontSize: "13px", fontWeight: "600", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}
                                        onClick={() => setEditBannerMode("upload")}
                                    >
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                                        <span>Upload New File</span>
                                    </button>
                                </div>

                                {editBannerMode === "url" ? (
                                    <div className="form-group">
                                        <label className="form-label">Banner Image URL / Path *</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="e.g. posters/1.jpg or https://..."
                                            value={editingBanner.customUrl || editingBanner.poster}
                                            onChange={(e) => setEditingBanner({ ...editingBanner, customUrl: e.target.value, poster: e.target.value })}
                                            required
                                        />
                                    </div>
                                ) : (
                                    <div className="form-group">
                                        <label className="form-label">Choose Replacement Image from Device</label>
                                        <div
                                            style={{
                                                border: "2px dashed var(--border-subtle)",
                                                borderRadius: "10px",
                                                padding: "20px 14px",
                                                textAlign: "center",
                                                cursor: "pointer",
                                                background: editBannerFile ? "rgba(16, 185, 129, 0.08)" : "rgba(255, 255, 255, 0.02)",
                                                transition: "all 0.2s"
                                            }}
                                            onClick={() => document.getElementById("edit-banner-file-input")?.click()}
                                        >
                                            <input
                                                id="edit-banner-file-input"
                                                type="file"
                                                accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
                                                style={{ display: "none" }}
                                                onChange={(e) => handleBannerFileSelect(e, true)}
                                            />
                                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={editBannerFile ? "var(--accent-emerald)" : "var(--primary-color)"} strokeWidth="1.8" style={{ margin: "0 auto 8px", display: "block" }}>
                                                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                                                <circle cx="8.5" cy="8.5" r="1.5"/>
                                                <polyline points="21 15 16 10 5 21"/>
                                            </svg>
                                            <div style={{ fontSize: "13.5px", fontWeight: "600", color: "#f8fafc", marginBottom: "4px" }}>
                                                {editBannerFile ? editBannerFile.name : "Click to Browse Replacement Image File"}
                                            </div>
                                            <div style={{ fontSize: "12px", color: "var(--text-muted)" }}>
                                                {editBannerFile ? `${(editBannerFile.size / 1024).toFixed(1)} KB — Click to choose different file` : "Supports JPG, PNG, WebP, GIF (Max 10MB)"}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">Display Order</label>
                                        <input
                                            type="number"
                                            min="1"
                                            className="form-control"
                                            value={editingBanner.order}
                                            onChange={(e) => setEditingBanner({ ...editingBanner, order: e.target.value })}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Banner Status (Active in Mobile App)</label>
                                        <select
                                            className="form-control"
                                            value={editingBanner.status}
                                            onChange={(e) => setEditingBanner({ ...editingBanner, status: parseInt(e.target.value, 10) })}
                                        >
                                            <option value={1}>🟢 Enabled (Show in App)</option>
                                            <option value={0}>⚪ Disabled (Hide from App)</option>
                                        </select>
                                    </div>
                                </div>

                                {/* Preview of banner */}
                                <div>
                                    <div style={{ fontSize: "12px", color: "var(--text-muted)", marginBottom: "6px", display: "flex", justifyContent: "space-between" }}>
                                        <span>Banner Image Preview:</span>
                                        {editBannerMode === "upload" && editBannerFile && (
                                            <span style={{ color: "var(--accent-emerald)", fontWeight: "600" }}>✓ Ready to upload replacement to CDN</span>
                                        )}
                                    </div>
                                    <div style={{ height: "120px", borderRadius: "8px", overflow: "hidden", border: "1px solid var(--border-subtle)", background: "rgba(0,0,0,0.2)" }}>
                                        <img
                                            src={editBannerMode === "upload" ? (editBannerFilePreview || getPosterUrl(editingBanner.poster)) : getPosterUrl(editingBanner.customUrl.trim() || editingBanner.poster)}
                                            alt="Preview"
                                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                            onError={(e) => {
                                                e.target.src = "https://public.nanbateam.com/posters/1.jpg";
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setIsEditBannerModalOpen(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-primary" disabled={bannerUpdating}>
                                    {bannerUpdating ? (editBannerMode === "upload" ? "Uploading & Saving..." : "Saving...") : "Save Banner Changes"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* LIVE STREAM VIDEO MONITOR */}
            {isLivePlayerOpen && (
                <LivePlayer
                    stream={selectedStation}
                    onClose={() => setIsLivePlayerOpen(false)}
                />
            )}

            {/* Toast Notice */}
            {toastMessage && (
                <div className="toast-notice">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--accent-emerald)" strokeWidth="2.5">
                        <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    <span>{toastMessage}</span>
                </div>
            )}
        </Layout>
    );
};

export default StationStudio;
